/**
 * Root component — §12.1, §12.2, §12.3.
 *
 * Controllers are registered once, at the root, through `@bib/injector`'s
 * provider list; §12.2 requires them to be "instantiated once under a root
 * owner and provided via context/injector".
 */

import { Show, lazy, type JSX } from 'solid-js';
import { Route, Router } from '@solidjs/router';
import { InjectionProvider, useInjectValue, useProvideMultiple } from '@bib/solid_js_injector';

import { AppShell, SignInScreen } from '~/components/AppShell';
import { createFeedController } from '~/state/feed-controller';
import { createSessionController } from '~/state/session-controller';
import { createToastController } from '~/state/toast-controller';
import { FEED, SESSION, TOAST } from '~/state/tokens';

const Home = lazy(() => import('~/routes/Home'));
const Feed = lazy(() => import('~/routes/Feed'));
const MatchDetail = lazy(() => import('~/routes/MatchDetail'));
const Digests = lazy(() => import('~/routes/Digests'));
const DigestDetail = lazy(() => import('~/routes/DigestDetail'));
const Profiles = lazy(() => import('~/routes/Profiles'));
const ProfileEditor = lazy(() => import('~/routes/ProfileEditor'));
const Sources = lazy(() => import('~/routes/Sources'));
const SourceWizard = lazy(() => import('~/routes/SourceWizard'));
const Runs = lazy(() => import('~/routes/Runs'));
const Settings = lazy(() => import('~/routes/Settings'));

/**
 * Everything below the injector, so `useInjectValue` can reach the session
 * before deciding whether to render the app or the sign-in screen.
 */
function Shell(props: { children: JSX.Element }): JSX.Element {
  const session = useInjectValue(SESSION);

  return (
    <Show when={!session.derivations.isLoading()} fallback={<div />}>
      <Show when={session.derivations.isAuthenticated()} fallback={<SignInScreen />}>
        <AppShell>{props.children}</AppShell>
      </Show>
    </Show>
  );
}

function Providers(props: { children: JSX.Element }): JSX.Element {
  useProvideMultiple([
    { provide: SESSION, useFactory: createSessionController },
    { provide: FEED, useFactory: createFeedController },
    { provide: TOAST, useFactory: createToastController },
  ]);
  return <>{props.children}</>;
}

export function App(): JSX.Element {
  return (
    <InjectionProvider scopeName="clotho">
      <Providers>
        <Router root={(routeProps) => <Shell>{routeProps.children}</Shell>}>
          <Route path="/" component={Home} />
          <Route path="/feed" component={Feed} />
          <Route path="/m/:matchId" component={MatchDetail} />
          <Route path="/digests" component={Digests} />
          <Route path="/digests/:id" component={DigestDetail} />
          <Route path="/profiles" component={Profiles} />
          <Route path="/profiles/:id" component={ProfileEditor} />
          <Route path="/sources" component={Sources} />
          <Route path="/sources/new" component={SourceWizard} />
          <Route path="/runs" component={Runs} />
          <Route path="/settings" component={Settings} />
        </Router>
      </Providers>
    </InjectionProvider>
  );
}
